import useClickOutside from 'hooks/useClickOutside';
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import Search from './Search';
import TruncateText from './TruncateText';
import { useTranslation } from 'react-i18next';
import UserShortCut from './UserShortCut';
export interface FiltersType {
  filters: {
    [key: string]: number[] | string[] | null;
  };
}
interface DropdownFilterProps {
  idPrefix: string;
  label: React.ReactNode;
  options: {
    id?: string | number;
    label?: string;
    value?: string;
    title?: string;
    text?: string;
    picture?: string;
    group?: string;
  }[];
  selectedOptions: string[] | number[] | null;
  setfilters: Dispatch<SetStateAction<FiltersType>>;
  filters: FiltersType;
  onSearch?: (searchValue: string) => void;
  className?: string;
  buttonClassName?: string;
  labelClassName?: string;
  showPicture?: boolean;
  containerClassName?: string;
}
const openFilterDropdowns = new Set<() => void>();
export const closeAllFilterDropdowns = (): void => {
  openFilterDropdowns.forEach((close) => close());
  openFilterDropdowns.clear();
};

if (typeof document !== 'undefined') {
  document.addEventListener('click', (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!target.closest('.minthr-icon-filter')) {
      closeAllFilterDropdowns();
    }
  });
}

const DropdownFilter: FC<DropdownFilterProps> = ({
  idPrefix,
  label,
  options,
  setfilters,
  filters,
  onSearch,
  className,
  buttonClassName,
  labelClassName,
  showPicture,
  containerClassName,
}) => {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const dropRef = useRef<HTMLDivElement>(null);
  useClickOutside(dropRef, setDropdownOpen, dropdownOpen);
  const closeThisDropdown = (): void => {
    setDropdownOpen(false);
    openFilterDropdowns.delete(closeThisDropdown);
  };
  function IsInArray(id: string, prevFilters: FiltersType): number[] | null {
    const currentIds = Array.isArray(prevFilters.filters[idPrefix])
      ? prevFilters.filters[idPrefix]
      : null;
    const parsedId = id;
    const newIds = currentIds
      ? currentIds.includes(parsedId as never)
        ? currentIds.filter((item) => item !== parsedId)
        : [...currentIds, parsedId]
      : [parsedId];
    return newIds.length > 0 ? (newIds as never) : null;
  }
  const toggleOption = (id: string): void => {
    if (id === '') {
      setfilters((prevFilters: { filters: object }) => {
        return {
          ...prevFilters,
          filters: {
            ...prevFilters.filters,
            [idPrefix]: null,
          },
        };
      });
    } else {
      setfilters((prevFilters: FiltersType) => {
        const newIds = IsInArray(id, prevFilters);
        return {
          ...prevFilters,
          filters: {
            ...prevFilters.filters,
            [idPrefix]: newIds,
          },
        };
      });
    }
  };
  const handleSearch = (value: string): void => {
    setSearchQuery(value);
    if (onSearch) {
      onSearch(value);
    }
  };
  // Filter options based on the search query
  const filteredOptions = options.filter((option) =>
    (option.text || option.title || '')
      .toLowerCase()
      .includes(searchQuery.toLowerCase()),
  );
  // Group filtered options by their `group` field (preserving first-seen order).
  // Empty groups are naturally excluded since they are built from filteredOptions.
  const hasGroups = filteredOptions.some((option) => option.group);
  const groupedOptions = hasGroups
    ? filteredOptions.reduce<{ group: string; options: typeof filteredOptions }[]>(
        (groups, option) => {
          const groupName = option.group || '';
          const existing = groups.find((g) => g.group === groupName);
          if (existing) {
            existing.options.push(option);
          } else {
            groups.push({ group: groupName, options: [option] });
          }
          return groups;
        },
        [],
      )
    : [];
  const toggleDropdown = (): void => {
    const next = !dropdownOpen;
    setDropdownOpen(next);
    if (next) {
      openFilterDropdowns.add(closeThisDropdown);
    } else {
      openFilterDropdowns.delete(closeThisDropdown);
    }
  };
  useEffect(() => {
    return () => {
      openFilterDropdowns.delete(closeThisDropdown);
    };
  }, []);

  const isActive =
    Array.isArray(filters.filters[idPrefix]) &&
    filters.filters[idPrefix].length > 0;
  const renderOption = (
    option: (typeof options)[number],
  ): React.ReactNode => (
    <li key={option.value || option.id}>
      <label
        className="dropdown-item minthr-flex minthr-flex-items-center minthr-py-1"
        onChange={() => {
          toggleOption(option.value || (option.id as string));
        }}
      >
        {showPicture && (
          <UserShortCut
            className="minthr-mr-2 minthr-avatar minthr-avatar-xs"
            userId={option.value}
            userPath={option.picture}
            alt=""
          />
        )}
        <span className="minthr-mr-5" style={{ flex: 1, minWidth: 0 }}>
          <TruncateText
            tag="span"
            maxLines={1}
            showTooltip={true}
            textTooltip={option.text || option.title}
          >
            {option.text || option.title}
          </TruncateText>
        </span>
        <label className="minthr-chackbox minthr-ml-auto minthr-pr-3 minthr-pb-2">
          <input
            name={`${idPrefix}_${option.value || option.id}`}
            type="checkbox"
            id={`${idPrefix}_${option.value || option.id}`}
            value={option.value || option.id}
            className="minthr-form-control"
            checked={
              filters.filters[idPrefix]?.includes(
                (option.value || option.id) as never,
              ) || false
            }
            readOnly
          />
          <label
            className="minthr-form-label minthr-text-hide minthr-p-0"
            onClick={() => {
              toggleOption(option.value || (option.id as string));
            }}
          >
            .
          </label>
        </label>
      </label>
    </li>
  );
  return (
    <div
      className={`${containerClassName} dropdown minthr-dropdown minthr-mr-2 minthr-mobile-mr-0 minthr-mobile-mb-2 minthr-mobile-px-2 minthr-mobile-w-50 minthr-icon-filter`}
      ref={dropRef}
    >
      <button
        type="button"
        className={` minthr-w-100 dropdown-toggle minthr-dropdown-btn minthr-btn-s minthr-border-rounded minthr-mobile-w-100 minthr-border-primary minthr-btn-filters ${
          isActive ? 'active filter-active show' : ''
        } ${buttonClassName}`}
        onClick={toggleDropdown}
        aria-expanded={dropdownOpen}
      >
        <span className={labelClassName}>{label}</span>
        {filters?.filters[idPrefix] &&
          filters?.filters[idPrefix]?.length > 0 && (
            <a className="filter-close" onClick={() => toggleOption('')}>
              <img
                className={`${isActive ? '' : 'minthr-icon-gray'}`}
                src="/icons/close.svg"
                alt=""
              />
            </a>
          )}
      </button>
      {dropdownOpen && (
        <ul
          className={`dropdown-menu minthr-max-w-400 minthr-dropdown-checklist minthr-overflow-auto minthr-mobile-list-style ${className}`}
          style={{
            display: 'inline-block',
          }}
        >
          <li className="">
            <Search
              value={searchQuery}
              onSearch={handleSearch}
              placeholder={t('Search')}
              className="minthr-searchbar-s minthr-mb-1"
              inputClassName="minthr-w-100"
            />
          </li>
          {options &&
            (hasGroups
              ? groupedOptions.map((group) => (
                  <React.Fragment key={group.group}>
                    <li
                      className="minthr-dropdown-group-title minthr-px-3 minthr-py-2 minthr-mt-1 text-m minthr-text-uppercase minthr-text-gray"
                      style={{ fontWeight: 700, letterSpacing: '0.04em' }}
                    >
                      {group.group}
                    </li>
                    {group.options.map(renderOption)}
                  </React.Fragment>
                ))
              : filteredOptions.map(renderOption))}
          {filteredOptions.length === 0 && (
            <li className="minthr-py-2 minthr-px-3 text-m minthr-text-center">
              {t('NoData.Empty')}
            </li>
          )}
        </ul>
      )}
    </div>
  );
};
export default DropdownFilter;
